import { format as originalFormat } from 'date-fns';
import * as React from 'react';
import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import { FlightTurnaroundDto, Resource, ResourceType, useQueryGetGetFlights } from '../../../../../backend';
import { errorToastConfig } from '../../../../../utils/utils';
import { useRmsGanttState, useRmsGanttValue, useSetRmsGantt } from '../../RmsContext';
import { ganttDayFormat } from '../gantt.constants';
import { getProposedAllocationByTurnaround, getResourceName, getTurnaroundById, getTurnaroundId } from '../gantt.utils';
import { GanttTurnaroundsBox } from './GanttTurnaroundRow.style';
import { allocateTurnaround } from './GanttTurnaroundRow.utils';
import { GanttTurnaroundBox } from './TurnaroundBox/GanttTurnaroundBox';
import { ScheduledTurnaroundBox } from './TurnaroundBox/ScheduledTurnaroundBox/ScheduledTurnaroundBox';
import { useUpdateTurnaround } from './useUpdateTurnaround';

export const GanttTurnaroundRow = memo(function GanttTurnaroundRow({
    turnaroundsOnRow,
    resource,
    index,
}: {
    turnaroundsOnRow: FlightTurnaroundDto[];
    resource?: Resource;
    index?: number;
}) {
    const setIsDragging = useSetRmsGantt('isDragging');
    const [proposedAllocation, setProposedAllocation] = useRmsGanttState('proposedAllocation');
    const [nbUpdateAllocations, setNbUpdateAllocations] = useRmsGanttState('nbUpdateAllocations');
    const rmsFilters = useRmsGanttValue('rmsFilters');
    const resourceType = useRmsGanttValue('resourceType');
    const day = useRmsGanttValue('day');

    const { data: ganttDto } = useQueryGetGetFlights({
        selectedDay: originalFormat(day, ganttDayFormat),
        selectedResourceType: resourceType,
    });
    const turnarounds = ganttDto?.turnarounds;
    const { updateFlights } = useUpdateTurnaround({ optimisticUpdate: true });

    const handleUpdatedTurnaround = useCallback(
        (updatedTurnaround: FlightTurnaroundDto, resourceName: string, resourceType: ResourceType) => {
            updateFlights({
                selectedDay: originalFormat(day, ganttDayFormat),
                selectedResourceType: resourceType,
                flightsUpdateDto: {
                    turnaround: updatedTurnaround,
                    resourceTypes: [resourceType],
                    updateBoardingStart: false,
                    updateBoardingEnd: false,
                },
            });
        },
        [day, updateFlights],
    );

    const updateNbAllocations = useCallback(
        (proposedAllocationForTurnaround: string, turnaround: FlightTurnaroundDto) => {
            if (proposedAllocationForTurnaround?.length >= 0) {
                if (
                    proposedAllocationForTurnaround !== getResourceName(turnaround) &&
                    getResourceName(turnaround) === resource?.name
                ) {
                    if (!!turnaround.arrivalFlight && !!turnaround.departureFlight) {
                        setNbUpdateAllocations(nbUpdateAllocations - 2);
                    } else {
                        setNbUpdateAllocations(nbUpdateAllocations - 1);
                    }
                }
                if (
                    proposedAllocationForTurnaround === getResourceName(turnaround) &&
                    getResourceName(turnaround) !== resource?.name
                ) {
                    if (!!turnaround.arrivalFlight && !!turnaround.departureFlight) {
                        setNbUpdateAllocations(nbUpdateAllocations + 2);
                    } else {
                        setNbUpdateAllocations(nbUpdateAllocations + 1);
                    }
                }
            } else if (getResourceName(turnaround) !== resource?.name) {
                if (!!turnaround.arrivalFlight && !!turnaround.departureFlight) {
                    setNbUpdateAllocations(nbUpdateAllocations + 2);
                } else {
                    setNbUpdateAllocations(nbUpdateAllocations + 1);
                }
            }
        },
        [nbUpdateAllocations, resource?.name, setNbUpdateAllocations],
    );

    //obligatory for the drop effect
    const dragOverHandler = useCallback(event => {
        event.preventDefault();
    }, []);
    const dropHandler = useCallback(
        (event, resourceName?: string) => {
            event.preventDefault();
            const dataTransfer = event.dataTransfer.getData('text/plain');
            const data = dataTransfer.split(',');
            const id = data[0];
            const considerArrivalFlight = data[1];
            const considerDepartureFlight = data[2];
            if (id !== '') {
                // copy object for IHM purposes
                const draggable = document.getElementById(id);
                event.target.appendChild(draggable.cloneNode());
                const turnaround = getTurnaroundById(turnarounds, id, considerArrivalFlight, considerDepartureFlight);
                if (resource?.active || resource === undefined) {
                    //proposed allocations verification
                    if (nbUpdateAllocations !== 0) {
                        const proposedAllocationForTurnaround = getProposedAllocationByTurnaround(
                            proposedAllocation,
                            turnaround,
                        );
                        const newProposition = proposedAllocation?.filter(o => o !== proposedAllocationForTurnaround);
                        const resource = resourceName?.length > 0 ? resourceName : '';
                        setProposedAllocation([
                            ...newProposition,
                            {
                                turnaround: {
                                    arr: turnaround?.arrivalFlight?.id,
                                    dep: turnaround?.departureFlight?.id,
                                },
                                resource: resource,
                            },
                        ]);
                        updateNbAllocations(proposedAllocationForTurnaround?.resource, turnaround);
                    } else {
                        const updatedTurnaround = allocateTurnaround(turnaround, resourceName, resourceType);
                        handleUpdatedTurnaround(updatedTurnaround, resourceName, resourceType);
                    }
                } else {
                    toast(<FormattedMessage id={'allocation.error.deactivated.resource'} />, errorToastConfig);
                }
                setIsDragging(false);
            }
        },
        [
            turnarounds,
            resource,
            setIsDragging,
            nbUpdateAllocations,
            proposedAllocation,
            setProposedAllocation,
            updateNbAllocations,
            resourceType,
            handleUpdatedTurnaround,
        ],
    );

    return (
        <GanttTurnaroundsBox
            id={resource?.name || index?.toString()}
            width={'100%'}
            onDragOver={dragOverHandler}
            onDrop={event => dropHandler(event, resource?.name)}
        >
            {turnaroundsOnRow.length !== 0 &&
                turnaroundsOnRow?.map((turnaround, i) => (
                    <GanttTurnaroundBox
                        key={`${getTurnaroundId(turnaround)}-${i}`}
                        turnaround={turnaround}
                        resource={resource}
                    />
                ))}
            {rmsFilters.viewModeScheduled === 'scheduled' &&
                turnaroundsOnRow.length !== 0 &&
                turnaroundsOnRow?.map((turnaround, i) => (
                    <ScheduledTurnaroundBox
                        key={`scheduled-${getTurnaroundId(turnaround)}-${i}`}
                        turnaround={turnaround}
                        minTimeBetweenUse={resource?.minTimeBetweenUse || 0}
                    />
                ))}
        </GanttTurnaroundsBox>
    );
});
